import React, { useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import classNames from 'classnames';
import { useMutation, useQuery } from '@apollo/client';
import { Trans } from '@lingui/macro';
import { useHistory } from 'react-router';
import { Alert } from 'antd';

import { Card } from 'components/AntD';
import { Query } from 'types';
import { UPDATE_REFERENCE_IS_PUBLIC } from 'api/candidate_api';
import { RETURN_CANDIDATE_REFERENCER_USER_FROM_TOKEN } from 'api/user_api';
import { FETCH_SHAREABLE_CRRS_FOR_ME } from 'api/candidate_shares_api';
import { REQUIRED_SUBMITTED_REFERENCES } from 'config/AppConfig';
import { trackEvent } from 'analytics/analyticsEvents';
import { returnValidReferenceRequestsForRelations } from '../../SharedFunctions';
import { getNetworkError } from 'util/utilFunctions';
import ProfileCard from 'components/CandidatePublicProfileComponents/ProfileCard';
import SplashScreen from 'components/SplashScreen';
import CandidateSharePageWrapper from '../CandidateSharePage';
import './styles.less';

export const PUBLIC_PROFILE_SETTINGS_REFERENCES_UPDATED_EVENT =
  'Candidate Experience Public Profile Settings Public References Updated';

const trackUpdatePublicReferencesEvent = debounce(() => {
  trackEvent(PUBLIC_PROFILE_SETTINGS_REFERENCES_UPDATED_EVENT);
}, 1000);

export default function PublicProfileSettings({
  numSubmittedReferences,
}: PublicProfileSettingsProps): JSX.Element {
  const history = useHistory();
  const [updateReferenceIsPublic] = useMutation(UPDATE_REFERENCE_IS_PUBLIC);
  const { data: userData } = useQuery<Pick<Query, 'returnUserFromToken'>>(
    RETURN_CANDIDATE_REFERENCER_USER_FROM_TOKEN
  );
  const { data: shareableCRRsForMe, refetch } = useQuery<Pick<Query, 'fetchShareableCRRsForMe'>>(
    FETCH_SHAREABLE_CRRS_FOR_ME
  );

  const onChange = useCallback(
    async (_selected, lastSelected) => {
      try {
        await updateReferenceIsPublic({
          variables: {
            input: {
              candidateReferencerRelationId: +lastSelected.id,
              isPublic: !lastSelected.isPublic,
            },
          },
        });
        trackUpdatePublicReferencesEvent();
        refetch && refetch();
      } catch (error) {
        console.error(error);
        alert(`Error updating status: ${getNetworkError(error)}`);
      }
    },
    [refetch, updateReferenceIsPublic]
  );

  const references = useMemo(
    () =>
      returnValidReferenceRequestsForRelations(shareableCRRsForMe?.fetchShareableCRRsForMe || []),
    [shareableCRRsForMe]
  );

  if (!userData) {
    return <SplashScreen />;
  }

  const { returnUserFromToken: user } = userData;
  const { candidateProfile, firstName } = user;
  const { candidateProfileId, candidatePhoto } = candidateProfile;

  return (
    <div className="public-profile-settings tw-flex tw-flex-col tw-gap-4">
      {numSubmittedReferences < REQUIRED_SUBMITTED_REFERENCES && (
        <Alert
          className="reference-toast"
          message={
            <Trans id="share_requirements">
              To share your profile, you must submit at least 2 references.
            </Trans>
          }
        />
      )}
      <div
        className={classNames({
          overlay: numSubmittedReferences < REQUIRED_SUBMITTED_REFERENCES,
        })}
      >
        <ProfileCard
          candidateProfileId={candidateProfileId}
          candidateFirstName={firstName}
          candidateProfile={candidateProfile}
          candidatePhoto={candidatePhoto}
          candidateReferencerRelations={references}
          onChange={onChange}
        />
        <br />
        <br />
        <Card id="share" className="candidate-share-page-wrapper">
          <CandidateSharePageWrapper
            numSubmittedReferences={numSubmittedReferences}
            history={history}
          />
        </Card>
      </div>
    </div>
  );
}

export interface PublicProfileSettingsProps {
  numSubmittedReferences: number;
}
